/**
 * Lead Command Center — safe, reusable lead actions.
 *
 * Everything here is demo-safe by construction:
 *   Email → compose dialog with Copy Draft / Open Email Client (mailto:) /
 *           Log Email — the demo itself never sends anything.
 *   Call  → tel: link so the device dials, plus a logged call action —
 *           the demo never places calls.
 * Notes/outcomes are recorded through the Record Interaction flow
 * (record-interaction.tsx), not here.
 */

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Copy, Loader2, Mail, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { lccApi, type LccLead } from '@/lib/lcc-api';

/** Demo teammates offered by the Assign control. */
export const LCC_ASSIGNEES = ['Demo Admin', 'Jordan Reyes', 'Casey Nguyen'] as const;

export function useLccInvalidate() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['lcc'] });
    void queryClient.invalidateQueries({ queryKey: ['activity'] });
  };
}

// ---------- email (compose modal; never sends) ----------

export function EmailComposeDialog({
  lead,
  open,
  onOpenChange,
  onLogged,
}: {
  lead: LccLead;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after the email action is logged — e.g. to record the result. */
  onLogged?: () => void;
}) {
  const invalidate = useLccInvalidate();
  const defaultSubject = `Re: ${lead.title}`;
  const defaultBody = useMemo(() => {
    const first = (lead.contactName ?? '').split(' ')[0] || 'there';
    return [
      `Hi ${first},`,
      '',
      `Thanks for reaching out${lead.companyName ? ` about ${lead.companyName}` : ''} — happy to help.`,
      lead.message ? `You mentioned: "${lead.message}"` : '',
      '',
      'Would a quick call this week work to go over next steps?',
      '',
      'Best,',
      'One Up Digital Marketing',
    ]
      .filter((line, i, arr) => !(line === '' && arr[i - 1] === ''))
      .join('\n');
  }, [lead]);

  const [subject, setSubject] = useState(defaultSubject);
  const [body, setBody] = useState(defaultBody);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (open) {
      setSubject(defaultSubject);
      setBody(defaultBody);
      setCopied(false);
    }
  }, [open, defaultSubject, defaultBody]);

  const logMutation = useMutation({
    mutationFn: () => lccApi.logEmailAction(lead.id, { subject, note: 'Draft composed in the Lead Command Center.' }),
    onSuccess: () => {
      invalidate();
      onOpenChange(false);
      onLogged?.();
    },
  });

  const mailto = `mailto:${encodeURIComponent(lead.email ?? '')}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Send email — safe mode</DialogTitle>
          <DialogDescription>
            The demo never sends email. Copy the draft or open it in your own email client, then
            log the action to the lead timeline.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <div className="text-[12px] text-muted-foreground">
            To: <span className="text-foreground">{lead.email ?? 'no email on file'}</span>
          </div>
          <input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="flex h-9 w-full rounded-md border border-input bg-background px-2.5 text-[13px] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            placeholder="Subject"
          />
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={8}
            className="flex w-full resize-y rounded-md border border-input bg-background px-2.5 py-1.5 text-[13px] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          />
        </div>
        <DialogFooter className="flex-wrap gap-1.5">
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="gap-1.5"
            onClick={() => {
              void navigator.clipboard.writeText(`Subject: ${subject}\n\n${body}`).then(() => {
                setCopied(true);
                window.setTimeout(() => setCopied(false), 1500);
              });
            }}
          >
            <Copy className="h-3.5 w-3.5" aria-hidden />
            {copied ? 'Copied!' : 'Copy Draft'}
          </Button>
          <Button type="button" size="sm" variant="outline" className="gap-1.5" asChild>
            <a href={mailto}>
              <Mail className="h-3.5 w-3.5" aria-hidden />
              Open Email Client
            </a>
          </Button>
          <Button
            type="button"
            size="sm"
            className="gap-1.5"
            disabled={logMutation.isPending}
            onClick={() => logMutation.mutate()}
          >
            {logMutation.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />}
            Log Email Action
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- call (tel: + logged action; never dials itself) ----------

export function CallButton({
  lead,
  label = 'Call',
  size = 'sm',
  variant = 'outline',
  className,
  onLogged,
}: {
  lead: LccLead;
  label?: string;
  size?: 'sm' | 'default';
  variant?: 'outline' | 'default' | 'ghost';
  className?: string;
  /** Called after the call action is logged — e.g. to record the result. */
  onLogged?: () => void;
}) {
  const invalidate = useLccInvalidate();
  const phone = lead.phone ?? lead.contact?.phone;
  const logMutation = useMutation({
    mutationFn: () => lccApi.logCallAction(lead.id, { outcome: 'call_initiated' }),
    onSuccess: () => {
      invalidate();
      onLogged?.();
    },
  });

  if (!phone) {
    return (
      <Button type="button" size={size} variant={variant} className={className} disabled title="No phone number on file">
        <Phone className="h-3.5 w-3.5" aria-hidden />
        {label}
      </Button>
    );
  }
  return (
    <Button type="button" size={size} variant={variant} className={className} asChild>
      <a
        href={`tel:${phone.replace(/[^+\d]/g, '')}`}
        onClick={() => logMutation.mutate()}
        title={`Dial ${phone} on this device (logged to the timeline)`}
      >
        <Phone className="h-3.5 w-3.5" aria-hidden />
        {label}
      </a>
    </Button>
  );
}

// ---------- quick assign select ----------

export function QuickAssignSelect({
  lead,
  triggerClassName,
}: {
  lead: LccLead;
  triggerClassName?: string;
}) {
  const invalidate = useLccInvalidate();
  const mutation = useMutation({
    mutationFn: (assignedTo: string) =>
      lccApi.updateLead(lead.id, { assignedTo: assignedTo === '__unassigned' ? null : assignedTo }),
    onSuccess: invalidate,
  });
  return (
    <Select
      value={lead.assignedTo ?? '__unassigned'}
      onValueChange={(value) => mutation.mutate(value)}
    >
      <SelectTrigger className={triggerClassName ?? 'h-8 w-[150px] text-[12px]'} disabled={mutation.isPending}>
        <SelectValue placeholder="Assign…" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="__unassigned">Unassigned</SelectItem>
        {LCC_ASSIGNEES.map((name) => (
          <SelectItem key={name} value={name}>
            {name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
